/**
 * Anonymous onboarding API calls (public self-service funnel).
 *
 * These are the only pre-identity endpoints the app talks to. They are called
 * with `skipAuth` so no stale bearer token is attached. Payment activation is
 * NOT performed here — it happens server-side on a verified webhook; the return
 * page only polls for the resulting status.
 */

import { httpClient, HttpError } from '@/utils/httpClient';
import type { ApiEnvelope, ApiListData } from '@/types/api';

/** A publicly offerable plan (payment-gateway fields intentionally omitted). */
export interface OnboardingPlan {
  plan_id: number;
  name: string;
  price: number;
  /** Billing period length in days (30 = monthly, 365 = yearly). */
  duration: number;
  description?: string;
  tagline?: string;
  plan_type?: string;
  is_qr_type_static?: number;
  is_qr_type_dynamic?: number;
  static_qr_limit?: number;
  dynamic_qr_limit?: number;
  staff_member_limit?: number;
  activestatus?: string;
}

/**
 * How this instance takes on new clients.
 * - `linked`: the browser is handed to the payment platform's hosted page.
 * - `manual`: nothing is charged online; the visitor leaves a request and a
 *   person creates and charges the client.
 */
export type PaymentMode = 'linked' | 'manual';

export interface SignupRequestInput {
  full_name: string;
  email: string;
  company_name: string;
  contact_no?: string;
  plan_id?: number;
  notes?: string;
}

export interface RegisterClientInput {
  full_name: string;
  email: string;
  company_name: string;
  contact_no?: string;
  plan_id: number;
}

export interface RegisterClientResult {
  user_id: number;
  organization_id: number;
  plan_id: number;
}

/**
 * List the plans offered to prospective clients. Only publicly offerable plans
 * are returned; the API answers 404 when there are none, mapped to an empty list.
 */
export async function fetchPublicPlans(): Promise<OnboardingPlan[]> {
  try {
    const response = await httpClient.get<ApiEnvelope<ApiListData<OnboardingPlan> | OnboardingPlan[]>>(
      '/plans',
      { skipAuth: true }
    );
    const payload = response.data?.data;
    const rows = Array.isArray(payload) ? payload : payload?.rows ?? [];
    return rows.filter((plan) => (plan.activestatus ?? 'A') === 'A');
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      return [];
    }
    throw error;
  }
}

/**
 * Read how this instance takes on new clients, so the funnel can branch before
 * showing anything. The endpoint answers the mode and nothing else — no
 * address, no identifier, no credential — because it is anonymous.
 *
 * A 404 means the backend predates the setting, and the behaviour it has
 * always had is the linked one; anything else is a real failure and is raised
 * so the funnel can offer to retry instead of guessing which branch to show.
 */
export async function fetchPublicConfig(): Promise<PaymentMode> {
  try {
    const response = await httpClient.get<ApiEnvelope<{ payment_mode?: string }>>(
      '/onboarding/config',
      { skipAuth: true }
    );
    return response.data?.data?.payment_mode === 'manual' ? 'manual' : 'linked';
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      return 'linked';
    }
    throw error;
  }
}

/**
 * Leave a sign-up request on an instance that does not charge online.
 *
 * Creates no account, no identity and no session, and answers no identifier:
 * a person reads the request and decides. The answer is the same whether or
 * not the address is already known, so it cannot be used to find out who our
 * clients are.
 */
export async function submitSignupRequest(input: SignupRequestInput): Promise<void> {
  const response = await httpClient.post<ApiEnvelope>('/onboarding/signup-request', input, {
    skipAuth: true,
  });
  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message || 'onboarding.signup.error_request_generic');
  }
}

/**
 * Register a prospective client. The backend creates the whole account in one
 * transaction (client + first organization + pending plan) and provisions the
 * external identity. Nothing is usable until payment confirms.
 */
export async function registerClient(input: RegisterClientInput): Promise<RegisterClientResult> {
  const response = await httpClient.post<ApiEnvelope<RegisterClientResult>>(
    '/onboarding/register-client',
    input,
    { skipAuth: true }
  );
  const { status, data, message } = response.data;
  if (status !== 'success' || !data) {
    throw new Error(message || 'onboarding.signup.error_generic');
  }
  return data;
}

/**
 * Start the payment checkout for a freshly registered account. Returns the URL
 * the browser must be sent to. The gateway returns to /payment/confirming.
 */
export async function startCheckout(input: { user_id: number; plan_id: number }): Promise<string> {
  const response = await httpClient.post<ApiEnvelope & { payUrl?: string }>(
    '/payments/checkout',
    input,
    { skipAuth: true }
  );
  const payUrl = response.data?.payUrl;
  if (!payUrl) {
    throw new Error(response.data?.message || 'onboarding.signup.error_generic');
  }
  return payUrl;
}

/**
 * Poll whether the payment for a checkout session has been confirmed (and the
 * account therefore activated) by the server-side webhook. Read-only.
 */
export async function pollPaymentActivated(sessionId: string): Promise<boolean> {
  const response = await httpClient.post<ApiEnvelope<{ activated: boolean }>>(
    '/payments/successPayment',
    { sessionId },
    { skipAuth: true }
  );
  return Boolean(response.data?.data?.activated);
}
